import {Component, OnInit, TemplateRef} from '@angular/core';
import {ModalService} from '../../../services/modal/modal.service';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {PropertiesService} from '../../../services/api/properties-service.service';
import {AlertService} from '../../../services/notify/alert.service';
import {CatalogItemModel, CatalogModel} from '../../../models/property.model';
import {HelpersService} from '../../../services/helpers.service';
import {ModalSettingsModel} from '../../../models/modal.model';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-catalogs',
    templateUrl: './catalogs.component.html',
    styleUrls: ['./catalogs.component.scss']
})
export class CatalogsComponent implements OnInit {

    description: string = null;
    allCatalogs: CatalogModel[] = [];

    selectedCatalog: CatalogModel;
    manageCatalog: CatalogModel;
    selectedCatalogItem: CatalogItemModel;

    constructor(
        public modalService: ModalService,
        private propertyService: PropertiesService,
        private alert: AlertService,
        public helper: HelpersService,
        public translate: TranslateService,
    ) {
    }

    ngOnInit(): void {
        this.getAllCatalogs();
    }

    getAllCatalogs(): void {
        this.propertyService.getAllCatalogs().subscribe({
            next: res => {
                this.allCatalogs = res.catalog;
                if (this.selectedCatalog) {
                    this.selectedCatalog = this.allCatalogs.find(x => x.id === this.selectedCatalog.id);
                }
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    selectCatalog(catalog: CatalogModel): void {
        if (!this.selectedCatalog || this.selectedCatalog.id !== catalog.id) {
            this.selectedCatalog = catalog;
        } else {
            this.selectedCatalog = null;
        }
    }

    openEditModal(isCatalog: boolean, item: CatalogModel | CatalogItemModel, content: TemplateRef<any>, e: MouseEvent): void {
        e.stopPropagation();
        e.preventDefault();
        if (isCatalog) {
            this.manageCatalog = structuredClone(item as CatalogModel);
        } else {
            this.selectedCatalogItem = structuredClone(item as CatalogItemModel);
        }

        this.modalService.openTemplateModal(content);
    }

    createCatalog(name: string, isOpen: boolean, modal: NgbModalRef): void {
        const data = {
            title: name,
            description: this.description,
            is_open: !isOpen,
        };
        this.propertyService.createNewCatalog(data).subscribe({
            next: res => {
                this.getAllCatalogs();
                this.alert.setAlert(res.message, 'green');
                modal.close();
                this.description = null;
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    deleteCatalog(catalog: CatalogModel, e: MouseEvent): void {
        e.stopPropagation();
        e.preventDefault();

        const modalSettings: ModalSettingsModel = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE'),
            modalBody: this.translate.instant('CONFIRM.TEXT.DELETE_CATALOG', {title: catalog.title}),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                const modalForceSettings: ModalSettingsModel = {
                    modalHeader: this.translate.instant('CONFIRM.HEADERS.FORCE'),
                    modalBody: this.translate.instant('CONFIRM.TEXT.DELETE_CATALOG_FORCE'),
                    buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
                    buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
                };
                const forceModal = this.modalService.createFromSettings(modalForceSettings);

                forceModal.result.then(() => {
                    this.deleteRequest(catalog.id, true);
                }).catch(err => {
                    if (err === false) {
                        this.deleteRequest(catalog.id, false);
                    }
                });
            }
        }).catch(() => {
            return;
        });
    }

    deleteRequest(catalogId: number, force: boolean): void {
        this.propertyService.deleteCatalog(catalogId, force).subscribe({
            next: res => {
                this.getAllCatalogs();
                this.alert.setAlert(res.message, 'green');
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    deleteCatalogItem(catalogItem: CatalogItemModel): void {
        const modalSettings: ModalSettingsModel = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE'),
            modalBody: this.translate.instant('CONFIRM.TEXT.DELETE_ITEM', {items_text: catalogItem.items_text}),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                this.propertyService.deleteCatalogItem(catalogItem.id).subscribe({
                    next: res => {
                        this.getAllCatalogs();
                        this.alert.setAlert(res.message, 'green');
                    }, error: err => {
                        this.alert.setAlert(err.message, 'blue');
                    }
                });
            }
        }).catch(() => {
            return;
        });
    }

    editCatalog(modal: NgbModalRef): void {
        const data = {
            title: this.manageCatalog.title,
            description: this.description,
            is_open: this.manageCatalog.is_open,
        };
        this.propertyService.editCatalog(this.manageCatalog.id, data).subscribe({
            next: () => {
                this.getAllCatalogs();
                this.description = null;
                this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'green');
                modal.close();
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    createCatalogItem(name: string, modal: NgbModalRef): void {
        const data = {
            catalog_id: this.manageCatalog.id,
            items_text: name,
        };
        this.propertyService.createCatalogItem(data).subscribe({
            next: res => {
                this.getAllCatalogs();
                this.alert.setAlert(res.message, 'green');
                modal.close();
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    editCatalogItem(modal: NgbModalRef): void {
        this.propertyService.editCatalogItem(this.selectedCatalogItem.id, this.selectedCatalogItem.items_text).subscribe({
            next: () => {
                this.getAllCatalogs();
                this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'green');
                modal.close();
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }
}
